import {
  emitCallsRuntimeEvent,
  emitGovernorLiveAction,
} from '@/lib/governorEventBus';
import { App as CapacitorApp } from '@capacitor/app';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Send, Search, Users, ArrowLeft, Loader2, Inbox, Crown, Phone, Video, UserPlus, PhoneIncoming, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import {
  acceptRingingCallSession,
  endCallSession,
  markRingingCallMissed,
} from '@/governor/calls/CallsLifecycleAuthority';
import { subscribeWithRetry } from '@/lib/realtime';
import { useMembership } from '@/contexts/MembershipContext';
import { useAuth } from '@/contexts/AuthContext';
import CallModal from './CallModal';
import { observeCallsRuntimeHandler } from '@/governor/calls/CallsRuntimeObservationBridge';
import {
  callsIncomingChannel,
} from '@/governor/calls/CallsRealtimeChannels';

interface Member {
  email: string;
  name: string | null;
}

interface Message {
  id: number;
  sender_email: string;
  sender_name: string;
  recipient_email: string;
  recipient_name: string | null;
  body: string;
  created_at: string;
  read: boolean;
}

interface MessagesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTarget?: { memberId?: string | null; email?: string | null; name?: string | null } | null;
  initialIncomingCall?: IncomingCall | null;
}

interface IncomingCall {
  fromEmail: string;
  fromName: string;
  mode: 'voice' | 'video';
  roomName?: string;
  callSessionId?: string | null;
  call_session_id?: string | null;
}


const makeCallRoomName = (a: string, b: string) =>
  'kc-' + [a, b].sort().join('-').toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 80);

const initials = (name: string | null, email: string) => {
  const source = (name && name.trim()) || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] || '?').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const diff = (now.getTime() - d.getTime()) / 86400000;
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};


const Messages: React.FC<MessagesProps> = ({ open, onOpenChange, initialTarget, initialIncomingCall }) => {
  const { isMember, memberEmail, openMembershipModal } = useMembership();
  // realAuthUser is the *actual* Supabase auth user. Under DEV_BYPASS_AUTH the
  // membership context reports isMember=true with a synthesized email but
  // there is no real Supabase JWT. We must NOT open Realtime websocket
  // channels in that case — Realtime would reject/return non-Phoenix frames
  // and the supabase-js decoder crashes with `TypeError: n is not iterable`.
  const { user: realAuthUser } = useAuth();
  const hasRealSession = !!realAuthUser;
  const [myName, setMyName] = useState<string>('');
  const [members, setMembers] = useState<Member[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeEmail, setActiveEmail] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !initialTarget) return;
    setActiveName(initialTarget.memberName || initialTarget.name || null);

    if (initialTarget.email) {
      setActiveEmail(initialTarget.email);
      setActiveName(initialTarget.memberName || initialTarget.name || initialTarget.email);
      return;
    }

    const byName = (initialTarget.name || initialTarget.memberName)
      ? members.find(x => (x.name || "").trim().toLowerCase() === (initialTarget.name || initialTarget.memberName)!.trim().toLowerCase())
      : null;

    if (byName?.email) {
      setActiveEmail(byName.email);
      setActiveName(byName.name || byName.email);
      return;
    }

    if (!initialTarget.memberId) return;

    (async () => {
      const { data: row } = await supabase
        .from('family_graph_members')
        .select('id,name,email,claimed_by_user_id')
        .eq('id', initialTarget.memberId)
        .is('deleted_at', null)
        .maybeSingle();

      const directEmail = (row as any)?.email;
      if (directEmail) {
        setActiveEmail(directEmail);
        setActiveName((row as any)?.name || initialTarget.memberName || initialTarget.name || directEmail);
        return;
      }

      const claimedUserId = (row as any)?.claimed_by_user_id;
      if (!claimedUserId) return;

      const { data: prof } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', claimedUserId)
        .maybeSingle();

      if ((prof as any)?.email) {
        setActiveEmail((prof as any).email);
        setActiveName((row as any)?.name || initialTarget.memberName || initialTarget.name || (prof as any).email);
      }
    })();
  }, [open, initialTarget?.memberId, initialTarget?.email, initialTarget?.name, members]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteOpen, setMessagesOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const consumedIncomingCallUrlRef = useRef<string | null>(null);
  const [pendingNativeCallAction, setPendingNativeCallAction] =
    useState<'accept' | 'decline' | null>(null);
  const [callMode, setCallMode] = useState<'voice' | 'video'>('voice');
  const [callInitiator, setCallInitiator] = useState(true);
  const [callRoomName, setCallRoomName] = useState<string | null>(null);
  const [activeCallSessionId, setActiveCallSessionId] = useState<string | null>(null);
  const [callPeer, setCallPeer] = useState<{ email: string; name: string | null } | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);


  // Load my profile + other family graph message contacts
useEffect(() => {
  if (!open || !isMember || !memberEmail) return;

  let cancelled = false;

  (async () => {
    setLoading(true);
    try {
      const { data: myRow } = await supabase
        .from('memberships')
        .select('name')
        .eq('email', memberEmail)
        .limit(1)
        .maybeSingle();

      if (!cancelled) {
        if (myRow?.name) setMyName(myRow.name);
        else setMyName(memberEmail.split('@')[0]);
      }

      const { data: rows, error } = await supabase
        .from('family_graph_members')
        .select('id,name,email,claimed_by_user_id')
        .is('deleted_at', null)
        .limit(500);

      if (error) {
        console.error('[Messages] family_graph_members load failed', error);
        toast.error('Messages could not load family contacts', {
          description: error.message || 'Database contact lookup failed.',
        });
        return;
      }

      const mapped = (rows || [])
        .map((m: any) => {
          const email = m.email || null;
          return {
            email,
            name: m.name || email || 'Family member',
          };
        })
        .filter((m: any) =>
          m.email &&
          m.email.toLowerCase() !== memberEmail.toLowerCase()
        );

      console.debug('[Messages] loaded contacts', {
        count: mapped.length,
        currentUser: memberEmail,
        sample: mapped.slice(0, 5),
      });

      if (!cancelled) setMembers(mapped as Member[]);
    } catch (err: any) {
      console.error('[Messages] contact loader crashed', err);
      toast.error('Messages contact loader crashed', {
        description: err?.message || String(err),
      });
    } finally {
      if (!cancelled) setLoading(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [open, isMember, memberEmail]);

const ringAudioRef = React.useRef<HTMLAudioElement | null>(null);
const audioUnlockedRef = React.useRef(false);
const unlockCallAudio = async () => {
  if (audioUnlockedRef.current) return;
  try {
    const audio = ringAudioRef.current || new Audio('/sounds/ringtone.mp3');
    audio.loop = true;
    audio.volume = 1;
    ringAudioRef.current = audio;

    // unlock with user gesture
    await audio.play();
    audio.pause();
    audio.currentTime = 0;

    audioUnlockedRef.current = true;
    console.log('[KinsCircle Ring] audio unlocked');
  } catch (err) {
    console.error('[KinsCircle Ring] unlock failed:', err);
  }
};

const stopIncomingRing = () => {
  try {
    ringAudioRef.current?.pause();
    if (ringAudioRef.current) ringAudioRef.current.currentTime = 0;
  } catch {}
};

const ringIncomingCall = () => {
  console.log('[KinsCircle Ring] incoming call ring attempt', { unlocked: audioUnlockedRef.current });

  try {
    const didVibrate = navigator.vibrate?.([700, 250, 700, 250, 1000]);
    console.log('[KinsCircle Ring] vibrate result:', didVibrate);
  } catch (err) {
    console.error('[KinsCircle Ring] vibrate failed:', err);
  }

  try {
    stopIncomingRing();

    const audio = new Audio('/sounds/ringtone.mp3');
    audio.loop = true;
    audio.volume = 1;
    ringAudioRef.current = audio;

    audio.play()
      .then(() => console.log('[KinsCircle Ring] audio started'))
      .catch((err) => {
        console.error('[KinsCircle Ring] audio failed:', err);
        alert('Ringer blocked: ' + (err?.message || String(err)));
      });
  } catch (err) {
    console.error('[KinsCircle Ring] setup failed:', err);
    alert('Ringer setup failed: ' + (err?.message || String(err)));
  }
};

// Global incoming-call listener for this user.
  //
  // Re-enabled now that we have `subscribeWithRetry` (src/lib/realtime.ts)
  // wrapping `.subscribe()` with exponential-backoff and toast-on-failure.
  // If the `database_realtime` publication is missing or the websocket
  // decoder throws, the helper will tear the channel down, show a single
  // rate-limited toast, and the page will keep working — it will NOT
  // crash with `TypeError: n is not iterable` like the old code path.
  //
  // We still gate on `hasRealSession`: without a real Supabase JWT, the
  // Realtime server rejects the connection immediately, which is just
  // wasted retries.
  useEffect(() => {
    if (!isMember || !memberEmail) return;
    // Allow logged-in member email channel to listen for call events.
    // Some valid member sessions do not expose hasRealSession correctly on mobile.

    const channel = supabase
      .channel(callsIncomingChannel(memberEmail), {
        config: { broadcast: { self: false } },
      })
      .on('broadcast', { event: 'incoming-call' }, (payload) => {
        const data = (payload?.payload ?? {}) as Partial<IncomingCall>;

      try {
        emitGovernorLiveAction('incoming_call_detected', {
          source: 'Messages.incomingCallBroadcast',
          receiverEmail: memberEmail,
          callerEmail: data.fromEmail,
          roomName: data.roomName,
          mode: data.mode,
          timestamp: new Date().toISOString(),
        });
      } catch {}
        if (!data.fromEmail || !data.mode) return;

        const detectedSessionId =
          (data as any).callSessionId ||
          (data as any).call_session_id ||
          null;

        if (detectedSessionId) {
          emitCallsRuntimeEvent({
            source:
              'Messages.incomingCallBroadcast',
            stage:
              'INCOMING_CALL_DETECTED',
            status:
              'success',
            callSessionId:
              detectedSessionId,
            callerEmail:
              data.fromEmail,
            receiverEmail:
              memberEmail,
            roomName:
              (data as any).roomName ||
              undefined,
            mode:
              data.mode,
          });
        }

        // Ignore if a call is already in progress for this user.
        if (callOpen) return;
        ringIncomingCall();
        setIncomingCall({
          fromEmail: data.fromEmail,
          fromName: data.fromName || data.fromEmail,
          mode: data.mode,
          roomName: (data as any).roomName || null,
          callSessionId: (data as any).callSessionId || (data as any).call_session_id || null,
          call_session_id: (data as any).callSessionId || (data as any).call_session_id || null,
        } as any);
      });

    const unsubscribe = subscribeWithRetry(channel, {
      label: `incoming-call:${memberEmail}`,
      maxAttempts: 3,
      // Background channel — failure is non-blocking, so suppress the toast
      // here; FamilyContext's postgres_changes channel will toast for the
      // common "publication missing" case.
      toastOnFailure: false,
    });

    return unsubscribe;
  }, [isMember, memberEmail, callOpen]);




  // Native Android notification action -> hydrate and consume the
  // existing Messages-owned incoming-call workflow.
  useEffect(() => {
    let cancelled = false;
    let sub: { remove?: () => void } | undefined;

    const handleIncomingCallUrl = async (rawUrl: string) => {
      try {
        const url = new URL(rawUrl);
        const nativeAction = url.searchParams.get('nativeAction');
        const callSessionId =
          url.searchParams.get('callSessionId') ||
          url.searchParams.get('call_session_id');

        let roomName =
          url.searchParams.get('incomingCallRoom') ||
          url.searchParams.get('roomName');
        let mode =
          (url.searchParams.get('mode') || 'voice') as
            | 'voice'
            | 'video';
        let fromName =
          url.searchParams.get('from') ||
          url.searchParams.get('fromName') ||
          'Family';
        let fromEmail =
          url.searchParams.get('fromEmail') ||
          url.searchParams.get('callerEmail') ||
          '';

        if (
          callSessionId &&
          memberEmail &&
          (!roomName || !fromEmail)
        ) {
          const { data: row } = await supabase
            .from('call_sessions')
            .select(
              'id, caller_email, caller_name, mode, room_name, status',
            )
            .eq('id', callSessionId)
            .eq('receiver_email', memberEmail)
            .maybeSingle();

          if (cancelled) return;

          if (row) {
            roomName = row.room_name || roomName;
            mode =
              row.mode === 'video' ? 'video' : 'voice';
            fromEmail =
              row.caller_email || fromEmail;
            fromName =
              row.caller_name ||
              row.caller_email ||
              fromName;
          }
        }

        if (!roomName || !fromEmail) return;

        const action =
          nativeAction === 'accept' ||
          nativeAction === 'decline'
            ? nativeAction
            : null;

        const consumeKey = [
          roomName,
          fromEmail,
          mode,
          callSessionId || 'no-session',
          action || 'open',
        ].join(':');

        if (
          consumedIncomingCallUrlRef.current ===
          consumeKey
        ) {
          return;
        }

        if (callOpen && !action) return;

        consumedIncomingCallUrlRef.current =
          consumeKey;

        try {
          emitGovernorLiveAction(
            'governor_native_call_action_received',
            {
              source:
                'Messages.handleIncomingCallUrl',
              action: action || 'open',
              receiverEmail: memberEmail,
              callerEmail: fromEmail,
              roomName,
              callSessionId,
              timestamp: new Date().toISOString(),
            },
          );
        } catch {}

        try {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        } catch {}

        setIncomingCall({
          fromEmail,
          fromName,
          mode,
          roomName,
          callSessionId,
          call_session_id: callSessionId,
        });

        if (action) {
          setPendingNativeCallAction(action);
        } else {
          ringIncomingCall();
        }
      } catch {}
    };

    try {
      const current = window.location.href;
      if (
        current.includes('nativeAction=') ||
        current.includes('incomingCallRoom=') ||
        current.includes('roomName=')
      ) {
        void handleIncomingCallUrl(current);
      }
    } catch {}

    CapacitorApp.addListener(
      'appUrlOpen',
      ({ url }) => {
        void handleIncomingCallUrl(url);
      },
    )
      .then(listener => {
        sub = listener;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      try {
        sub?.remove?.();
      } catch {}
    };
  }, [callOpen, memberEmail]);


const loadMessages = useCallback(async () => {
    if (!memberEmail) return;
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`sender_email.eq.${memberEmail},recipient_email.eq.${memberEmail}`)
      .order('created_at', { ascending: true })
      .limit(500);
    if (!error && data) setMessages(data as Message[]);
  }, [memberEmail]);

  useEffect(() => {
    if (!open || !isMember || !memberEmail) return;
    // Don't poll the messages table without a real auth session — the
    // requests will fail with 401 / RLS errors under DEV_BYPASS_AUTH and
    // spam the console + network tab.
    if (!hasRealSession) return;
    setLoading(true);
    loadMessages().finally(() => setLoading(false));
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [open, isMember, memberEmail, loadMessages, hasRealSession]);


  useEffect(() => {
    if (!activeEmail || !memberEmail) return;
    const unread = messages.filter(
      m => m.sender_email === activeEmail && m.recipient_email === memberEmail && !m.read
    );
    if (unread.length === 0) return;
    (async () => {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('recipient_email', memberEmail)
        .eq('sender_email', activeEmail)
        .eq('read', false);
      setMessages(prev =>
        prev.map(m =>
          m.sender_email === activeEmail && m.recipient_email === memberEmail
            ? { ...m, read: true }
            : m
        )
      );
    })();
  }, [activeEmail, memberEmail, messages]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeEmail, messages]);

  const conversations = useMemo(() => {
    if (!memberEmail) return [];
    const map = new Map<
      string,
      { email: string; name: string | null; last: Message; unread: number }
    >();
    for (const m of messages) {
      const otherEmail = m.sender_email === memberEmail ? m.recipient_email : m.sender_email;
      const otherName = m.sender_email === memberEmail ? m.recipient_name : m.sender_name;
      const existing = map.get(otherEmail);
      const unreadIncrement =
        m.recipient_email === memberEmail && !m.read ? 1 : 0;
      if (!existing) {
        map.set(otherEmail, { email: otherEmail, name: otherName, last: m, unread: unreadIncrement });
      } else {
        existing.unread += unreadIncrement;
        if (new Date(m.created_at) > new Date(existing.last.created_at)) {
          existing.last = m;
          if (otherName) existing.name = otherName;
        }
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.last.created_at).getTime() - new Date(a.last.created_at).getTime()
    );
  }, [messages, memberEmail]);

  const directory = useMemo(() => {
    const emailsWithConvo = new Set(conversations.map(c => c.email));
    const byEmail = new Map<string, { email: string; name: string | null; last: Message | null; unread: number }>();

    for (const c of conversations) {
      byEmail.set(c.email, { email: c.email, name: c.name, last: c.last, unread: c.unread });
    }

    for (const m of members) {
      if (!m.email) continue;
      if (!byEmail.has(m.email)) {
        byEmail.set(m.email, { email: m.email, name: m.name, last: null as Message | null, unread: 0 });
      }
    }

    const targetEmail = activeEmail || initialTarget?.email || null;
    const targetName = initialTarget?.memberName || initialTarget?.name || targetEmail;
    if (targetEmail && !byEmail.has(targetEmail)) {
      byEmail.set(targetEmail, {
        email: targetEmail,
        name: targetName,
        last: null as Message | null,
        unread: 0,
      });
    }

    const combined = Array.from(byEmail.values());
    const q = search.trim().toLowerCase();
    if (!q) return combined;
    return combined.filter(
      e =>
        e.email.toLowerCase().includes(q) ||
        (e.name || '').toLowerCase().includes(q)
    );
  }, [conversations, members, search, initialTarget, activeEmail]);

  const activeThread = useMemo(() => {
    if (!activeEmail || !memberEmail) return [];
    return messages.filter(
      m =>
        (m.sender_email === memberEmail && m.recipient_email === activeEmail) ||
        (m.sender_email === activeEmail && m.recipient_email === memberEmail)
    );
  }, [messages, activeEmail, memberEmail]);

  const activeContact = useMemo(() => {
    if (!activeEmail) return null;
    const fromConvo = conversations.find(c => c.email === activeEmail);
    if (fromConvo) return { email: fromConvo.email, name: fromConvo.name };
    const fromMembers = members.find(m => m.email === activeEmail);
    return fromMembers || { email: activeEmail, name: null };
  }, [activeEmail, conversations, members]);

  const handleSend = async () => {
    if (!draft.trim() || !activeEmail || !memberEmail) return;
    setSending(true);
    const sendingSafetyTimer = window.setTimeout(() => {
      window.clearTimeout(sendingSafetyTimer);
    setSending(false);
    }, 8000);
    const optimistic: Message = {
      id: Date.now(),
      sender_email: memberEmail,
      sender_name: myName || memberEmail,
      recipient_email: activeEmail,
      recipient_name: activeContact?.name || null,
      body: draft.trim(),
      created_at: new Date().toISOString(),
      read: false,
    };
    setMessages(prev => [...prev, optimistic]);
    setDraft('');

    const { error } = await supabase.from('messages').insert({
      sender_email: memberEmail,
      sender_name: myName || memberEmail,
      recipient_email: activeEmail,
      recipient_name: activeContact?.name || null,
      body: optimistic.body,
      read: false,
    });

    if (callInviteError) {
      toast.error('Call invite message failed', { description: callInviteError.message });
    } else {
      toast.success('Call invite sent');
    }
    setSending(false);
    if (error) {
      toast.error('Could not send message', { description: error.message });
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } else {
      loadMessages();

      // Email notification for private family message
      fetch('/.netlify/functions/send-message-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: activeEmail,
          fromName: myName || memberEmail,
          message: optimistic.body,
        }),
      }).catch(() => {});


    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startCall = async (
    mode: 'voice' | 'video',
  ) => {
    if (
      !activeEmail ||
      !memberEmail ||
      !activeContact
    ) {
      return;
    }

    observeCallsRuntimeHandler(
      'Messages.startCall',
    );

    emitGovernorLiveAction(
      'governor_call_attempt',
      {
        source:
          'Messages.startCall',
        action:
          'route_to_native_call_authority',
        mode,
        callerEmail:
          memberEmail,
        receiverEmail:
          activeEmail,
        status:
          'delegated',
        timestamp:
          new Date().toISOString(),
      },
    );

    const params =
      new URLSearchParams({
        to:
          activeEmail,
        name:
          activeContact.name ||
          activeEmail,
        mode,
        origin:
          'messages',
      });

    onOpenChange(false);

    window.location.assign(
      `/native-call?${params.toString()}`,
    );
  };

  const acceptIncomingCall = async () => {
    stopIncomingRing();
    if (!incomingCall) return;

    observeCallsRuntimeHandler('Messages.acceptIncomingCall');

    const roomName = incomingCall.roomName || makeCallRoomName(memberEmail, incomingCall.fromEmail);

    try {
      localStorage.setItem(`kc:call-answering:${roomName}`, String(Date.now()));
      window.dispatchEvent(new CustomEvent('kc:call-answered', { detail: { roomName } }));
    } catch {}

    const callSessionId =
      incomingCall.callSessionId ||
      incomingCall.call_session_id ||
      null;

    setActiveCallSessionId(callSessionId);
    setCallRoomName(roomName);

    const acceptanceResult =
      await acceptRingingCallSession({
        callSessionId,
        callerEmail:
          incomingCall.fromEmail,
        receiverEmail: memberEmail,
        roomName,
      });

    emitCallsRuntimeEvent({
      source:
        'Messages.acceptIncomingCall',
      stage:
        acceptanceResult.ok
          ? 'RECEIVER_ACCEPTED'
          : 'RECEIVER_ACCEPT_FAILED',
      status:
        acceptanceResult.ok
          ? 'success'
          : 'failure',
      callSessionId:
        acceptanceResult.callSessionId ||
        callSessionId,
      callerEmail:
        incomingCall.fromEmail,
      receiverEmail:
        memberEmail,
      roomName,
      mode:
        incomingCall.mode ||
        callMode,
      error:
        acceptanceResult.error ||
        undefined,
    });

    if (!acceptanceResult.ok) {
      console.warn(
        '[KinsCircle Call] accept sync failed:',
        acceptanceResult.error,
      );
    }

    setIncomingCall(null);
    setCallMode((incomingCall.mode as 'voice' | 'video') || 'video');
  setCallInitiator(false);

  setCallPeer({ email: incomingCall.fromEmail, name: incomingCall.fromName || incomingCall.fromEmail });
    try {
      emitGovernorLiveAction(
        'governor_native_call_action_consumed',
        {
          source: 'Messages.acceptIncomingCall',
          action: 'accept',
          callSessionId,
          callerEmail: incomingCall.fromEmail,
          receiverEmail: memberEmail,
          status: 'accepted',
          timestamp: new Date().toISOString(),
        },
      );

      emitGovernorLiveAction('governor_call_connected', {
        source: 'Messages.acceptIncomingCall',
        state: 'accepted_to_live_modal',
        callerEmail: incomingCall.fromEmail,
        receiverEmail: memberEmail,
        mode: incomingCall.mode || callMode,
        callSessionId,
        timestamp: new Date().toISOString(),
      });
    } catch {}
  setCallOpen(true);
  };

  const closeNativeCall = async () => {
    stopIncomingRing();

    try {
      (document.activeElement as HTMLElement | null)?.blur?.();
    } catch {}

    const endedAt = new Date().toISOString();
    const activeStatuses = [
      'ringing',
      'accepted',
      'connecting',
      'connected',
    ];

    try {
      const endResult =
        await endCallSession({
          callSessionId:
            activeCallSessionId,
          callerEmail:
            callInitiator
              ? memberEmail
              : callPeer?.email,
          receiverEmail:
            callInitiator
              ? callPeer?.email
              : memberEmail,
          roomName:
            callRoomName,
        });

      if (!endResult.ok) {
        throw new Error(
          endResult.error ||
            'Call end synchronization failed.',
        );
      }

      emitGovernorLiveAction(
        'governor_native_call_action_consumed',
        {
          source: 'Messages.closeNativeCall',
          action: 'end',
          callSessionId: activeCallSessionId,
          roomName: callRoomName,
          callerEmail: callInitiator
            ? memberEmail
            : callPeer?.email,
          receiverEmail: callInitiator
            ? callPeer?.email
            : memberEmail,
          status: 'ended',
          returnedSurface: 'family-messages',
          timestamp: endedAt,
        },
      );
    } catch (error) {
      emitGovernorLiveAction(
        'governor_native_call_action_consumed',
        {
          source: 'Messages.closeNativeCall',
          action: 'end',
          callSessionId: activeCallSessionId,
          roomName: callRoomName,
          status: 'end_sync_failed',
          error:
            error instanceof Error
              ? error.message
              : String(error),
          timestamp: endedAt,
        },
      );
    }

    setCallOpen(false);
    setIncomingCall(null);
    setCallInitiator(false);
    setCallRoomName(null);
    setActiveCallSessionId(null);
    setCallPeer(null);

    try {
      window.dispatchEvent(
        new CustomEvent(
          'kc:call-returned-to-messages',
          {
            detail: {
              callSessionId: activeCallSessionId,
              roomName: callRoomName,
              timestamp: endedAt,
            },
          },
        ),
      );
    } catch {}
  };

  const declineIncomingCall = async () => {
  stopIncomingRing();

  observeCallsRuntimeHandler('Messages.declineIncomingCall');

  if (incomingCall && memberEmail) {
    const callSessionId =
      incomingCall.callSessionId ||
      incomingCall.call_session_id ||
      null;

    try {
      const declineResult =
        await markRingingCallMissed({
          callSessionId,
          callerEmail:
            incomingCall.fromEmail,
          receiverEmail:
            memberEmail,
          roomName:
            incomingCall.roomName,
        });

      if (!declineResult.ok) {
        throw new Error(
          declineResult.error ||
            'Call decline synchronization failed.',
        );
      }

      try {
        emitGovernorLiveAction(
          'governor_native_call_action_consumed',
          {
            source: 'Messages.declineIncomingCall',
            action: 'decline',
            callSessionId,
            callerEmail: incomingCall.fromEmail,
            receiverEmail: memberEmail,
            status: 'missed',
            timestamp: new Date().toISOString(),
          },
        );
      } catch {}
    } catch {}
  }

  setIncomingCall(null);
};

  useEffect(() => {
    if (!pendingNativeCallAction || !incomingCall) {
      return;
    }

    const action = pendingNativeCallAction;
    setPendingNativeCallAction(null);

    if (action === 'accept') {
      void acceptIncomingCall();
      return;
    }

    void declineIncomingCall();
  }, [pendingNativeCallAction, incomingCall]);

  useEffect(() => {
    if (!open || !initialIncomingCall) return;
    setIncomingCall(initialIncomingCall);
  }, [open, initialIncomingCall?.fromEmail, initialIncomingCall?.fromName, initialIncomingCall?.mode]);

  // Not a member → locked state
  if (!isMember) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#FAF6F0] to-[#F5EDE0] border-fuchsia-300">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-fuchsia-950 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-fuchsia-800" />
              Family Messages
            </DialogTitle>
            <DialogDescription className="text-fuchsia-800/70">
              Private member-to-member messaging, calls & video chat are part of your KinsCircle membership.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 flex items-center justify-center mb-4 shadow-lg">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <p className="text-sm text-fuchsia-900/80 mb-5">
              Send private notes and family updates inside your private KinsCircle space.
            </p>
            <Button
              onClick={() => {
                onOpenChange(false);
                openMembershipModal();
              }}
              className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:from-fuchsia-700 hover:to-amber-500 text-white gap-2 rounded-full"
            >
              <Crown className="w-4 h-4" />
              Become a Member · $9.99/member/yr
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent onPointerDown={unlockCallAudio} className="max-w-5xl p-0 overflow-hidden bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50 border-fuchsia-200 h-[85vh] flex flex-col shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-fuchsia-200/60 bg-gradient-to-r from-purple-100 via-fuchsia-100 to-pink-100">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-600 via-fuchsia-500 to-pink-400 flex items-center justify-center shadow-md">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="font-serif text-xl text-slate-900">Family Messages</DialogTitle>
                <DialogDescription className="text-xs text-slate-600">
                  Private conversations inside your KinsCircle
                </DialogDescription>
              </div>
            </div>

            <Button
              onClick={() => {
                  unlockCallAudio();
                  if (initialTarget?.email) setActiveEmail(initialTarget.email);
                }} disabled={!initialTarget?.email}
              variant="outline"
              className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 gap-1.5 rounded-full"
              size="sm"
            >
              <UserPlus className="w-4 h-4" /> Messages
            </Button>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar */}
            <aside
              className={`${
                activeEmail ? 'hidden md:flex' : 'flex'
              } w-full md:w-80 border-r border-fuchsia-200 bg-white/80 backdrop-blur flex-col`}
            >
              <div className="p-4 border-b border-fuchsia-200 space-y-2 bg-white/75">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white0/70" />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search family..."
                    className="pl-9 bg-white border-fuchsia-300"
                  />
                </div>
                <Button
                  onClick={() => {
                    if (initialTarget?.email) setActiveEmail(initialTarget.email);
                  }} disabled={!initialTarget?.email}
                  variant="ghost"
                  className="w-full justify-start text-fuchsia-900 hover:bg-fuchsia-100 gap-2"
                  size="sm"
                >
                  <UserPlus className="w-4 h-4" /> {activeName ? `Message ${activeName}` : initialTarget ? 'Opening conversation...' : 'No conversation selected'}
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading && (
                  <div className="flex items-center justify-center py-8 text-fuchsia-800/70 gap-2 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading conversations…
                  </div>
                )}

                {!loading && directory.length === 0 && (
                  <div className="p-6 text-center text-sm text-fuchsia-800/70">
                    <Users className="w-8 h-8 mx-auto mb-2 text-white0/70" />
                    <p className="mb-3">No other members yet.</p>
                    <Button
                      onClick={() => {}} disabled={true}
                      className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 text-white gap-2"
                      size="sm"
                    >
                      <UserPlus className="w-4 h-4" /> {activeName ? `Conversation with ${activeName}` : initialTarget ? 'Opening conversation...' : 'No conversation selected'}
                    </Button>
                  </div>
                )}

                {directory.map(entry => {
                  const isActive = entry.email === activeEmail;
                  return (
                    <button
                      key={entry.email}
                      onClick={() => setActiveEmail(entry.email)}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-fuchsia-900/5 text-left transition-colors ${
                        isActive ? 'bg-fuchsia-100/80' : 'hover:bg-fuchsia-50'
                      }`}
                    >
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 flex items-center justify-center text-white font-serif font-bold shadow shrink-0">
                        {initials(entry.name, entry.email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-fuchsia-950 truncate text-sm">
                            {entry.name || entry.email.split('@')[0]}
                          </div>
                          {entry.last && (
                            <span className="text-[10px] text-fuchsia-800/60 shrink-0">
                              {formatTime(entry.last.created_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className="text-xs text-slate-600 truncate">
                            {entry.last
                              ? `${entry.last.sender_email === memberEmail ? 'You: ' : ''}${entry.last.body}`
                              : entry.email}
                          </p>
                          {entry.unread > 0 && (
                            <Badge className="bg-rose-600 hover:bg-rose-600 text-white text-[10px] h-5 px-1.5 shrink-0">
                              {entry.unread}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Thread */}
            <section
              className={`${
                activeEmail ? 'flex' : 'hidden md:flex'
              } flex-1 flex-col bg-gradient-to-b from-fuchsia-50 via-pink-50 to-amber-50`}
            >
              {!activeEmail ? (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <Inbox className="w-12 h-12 mx-auto mb-3 text-white0/70" />
                    <div className="font-serif text-xl text-slate-900 mb-1">Your family inbox</div>
                    <p className="text-sm text-fuchsia-800/70 max-w-xs">
                      Pick a family member to start or continue a conversation, call, or video chat.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-fuchsia-200/70 bg-white/75">
                    <button
                      className="md:hidden p-1.5 -ml-1 rounded hover:bg-fuchsia-100"
                      onClick={() => setActiveEmail(null)}
                    >
                      <ArrowLeft className="w-5 h-5 text-fuchsia-900" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 flex items-center justify-center text-white font-serif font-bold text-sm shadow">
                      {initials(activeContact?.name || null, activeEmail)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-fuchsia-950 truncate">
                        {activeContact?.name || activeEmail.split('@')[0]}
                      </div>
                      <div className="text-xs text-fuchsia-800/60 truncate">{activeEmail}</div>
                    </div>
                    <button
                      onClick={() => startCall('voice')}
                      className="p-2 rounded-full hover:bg-fuchsia-100 text-fuchsia-900 transition-colors"
                      aria-label="Voice call"
                      title="Voice call"
                    >
                      <Phone className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => startCall('video')}
                      className="p-2 rounded-full hover:bg-fuchsia-100 text-fuchsia-900 transition-colors"
                      aria-label="Video call"
                      title="Video call"
                    >
                      <Video className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-3">
                    {activeThread.length === 0 && (
                      <div className="text-center text-sm text-fuchsia-800/70 py-10">
                        No messages yet. Say hello to your family!
                      </div>
                    )}
                    {activeThread.map((m, idx) => {
                      const mine = m.sender_email === memberEmail;
                      const prev = activeThread[idx - 1];
                      const showAvatar = !mine && (!prev || prev.sender_email !== m.sender_email);
              const isCallMessage = m.body?.toLowerCase().includes('call');
              const isVideoCall = m.body?.toLowerCase().includes('video');
              const callExpired = isCallMessage && Date.now() - new Date(m.created_at).getTime() > 30000;
                      return (
                        <div key={m.id} className={`flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
                          {!mine && (
                            <div className="w-7 h-7 shrink-0">
                              {showAvatar && (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 flex items-center justify-center text-white text-[10px] font-bold shadow">
                                  {initials(m.sender_name, m.sender_email)}
                                </div>
                              )}
                            </div>
                          )}
                          <div
                            className={`max-w-[75%] px-4 py-2 rounded-2xl shadow-sm ${
                              mine
                                ? 'bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white rounded-br-sm'
                                : 'bg-white text-fuchsia-950 border border-fuchsia-200 rounded-bl-sm'
                            }`}
                          >
                            {m.body?.toLowerCase().includes('call') ? (
  <div className="space-y-2">
    <p className="text-sm font-medium">
      {mine
        ? `📞 ${callExpired ? 'Missed' : isVideoCall ? 'Video' : 'Voice'} ${callExpired ? (isVideoCall ? 'video call' : 'voice call') : 'call invite — waiting for family member to join.'}`
        : `📞 Incoming ${m.body.toLowerCase().includes('video') ? 'video' : 'voice'} call`}
    </p>
    {!mine && (
      <button
        onClick={() => setCallPeer({ email: m.sender_email, name: m.sender_name || m.sender_email })}
        className="px-4 py-2 rounded-full bg-white text-purple-700 font-semibold text-sm"
      >
        Join Call
      </button>
    )}
  </div>
) : (
  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>
)}
                            <div className={`text-[10px] mt-1 ${mine ? 'text-fuchsia-100/70' : 'text-fuchsia-800/60'}`}>
                              {formatTime(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={threadEndRef} />
                  </div>

                  <div className="border-t border-fuchsia-200/70 bg-white/80 p-3">
                    <div className="flex items-end gap-2">
                      <textarea
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        placeholder="Write a message..."
                        className="flex-1 resize-none bg-white border border-fuchsia-300 rounded-2xl px-4 py-2.5 text-sm text-fuchsia-950 placeholder:text-fuchsia-800/50 focus:outline-none focus:ring-2 focus:ring-amber-500 max-h-32"
                      />
                      <Button
                        onClick={handleSend}
                        disabled={!draft.trim()}
                        className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:from-fuchsia-700 hover:to-amber-500 text-white rounded-full h-11 w-11 p-0 shrink-0"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-[10px] text-fuchsia-800/60 mt-1.5 px-2">
                      Press Enter to send · Shift+Enter for a new line
                    </p>
                  </div>
                </>
              )}
            </section>
          </div>
        </DialogContent>
      </Dialog>

      {/* Incoming call toast */}
      {incomingCall && !callOpen && (
        <div className="fixed bottom-6 right-6 z-[60] bg-white rounded-2xl shadow-2xl border border-fuchsia-300 p-4 w-80 animate-in slide-in-from-bottom-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 flex items-center justify-center text-white font-bold shadow">
              {initials(incomingCall.fromName, incomingCall.fromEmail)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-fuchsia-950 truncate">{incomingCall.fromName}</div>
              <div className="text-xs text-slate-600 flex items-center gap-1">
                <PhoneIncoming className="w-3 h-3 animate-pulse" />
                Incoming {incomingCall.mode} call…
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={declineIncomingCall} variant="outline" className="flex-1 border-red-300 text-red-700 hover:bg-red-50 gap-1.5">
              <X className="w-4 h-4" /> Decline
            </Button>
            <Button onClick={acceptIncomingCall} className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1.5">
              {incomingCall.mode === 'video' ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
              Accept
            </Button>
          </div>
        </div>
      )}

      {/* Call modal */}
      {callPeer && memberEmail && (
        <CallModal
          open={callOpen}
          onOpenChange={(open) => open ? setCallOpen(true) : closeNativeCall()}
          mode={callMode}
          selfEmail={memberEmail}
          selfName={myName || memberEmail}
          peerEmail={callPeer.email}
          peerName={callPeer.name}
          initiator={callInitiator}
          roomName={callRoomName || undefined}
          callSessionId={activeCallSessionId}
        />
      )}

      {/* Messages modal */}
    </>
  );
};

export default Messages;
